import React from 'react';
import { Input, Table, TableBody, TableCell, TableHeader, TableRow } from 'semantic-ui-react';
import If from 'Views/Components/If/If';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { UserAddOnPriceRow } from './userPricingRows';

interface UserAddOnsPricingTableProps {
	rows: UserAddOnPriceRow[];
	onPriceChange: (target: UserAddOnPriceRow, price: number | null) => void;
	isReadOnly?: boolean;
	errors?: { [key: string]: string | undefined };
}

/**
 * Form-state fork of the shared AdditionalOptionsTable (account-page variant — no space-units
 * column). Keeps the per-row string mirror so partially-typed decimals like "0." survive the
 * controlled input's reconciliation.
 */
export function UserAddOnsPricingTable({
	rows,
	onPriceChange,
	isReadOnly = true,
	errors,
}: UserAddOnsPricingTableProps) {
	const [priceText, setPriceText] = React.useState<Record<string, string>>({});

	const getRowText = (row: UserAddOnPriceRow): string => {
		const key = row.additionalBookingOptionId ?? '';
		const local = priceText[key];
		if (local !== undefined) {
			const localAsNumber = local === '' || local === '-' ? undefined : Number(local);
			const matchesModel = row.price === localAsNumber || (row.price == null && (local === '' || local === '-'));
			if (matchesModel) return local;
		}
		return row.price == null ? '' : String(row.price);
	};

	const handleChange = (row: UserAddOnPriceRow, next: string) => {
		const key = row.additionalBookingOptionId ?? '';
		setPriceText(prev => ({ ...prev, [key]: next }));
		if (next === '' || next === '-') {
			onPriceChange(row, null);
			return;
		}
		const parsed = Number(next);
		if (Number.isFinite(parsed)) {
			onPriceChange(row, parsed);
		}
	};

	return (
		<Table unstackable>
			<TableHeader>
				<TableRow>
					<TableCell textAlign="left">Ticket type</TableCell>
					<TableCell textAlign="left">Cost</TableCell>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map(row => {
					return (
						<TableRow key={row.additionalBookingOptionId}>
							<TableCell textAlign="left">{row.additionalBookingOptionName}</TableCell>
							<TableCell textAlign="left">
								<If condition={isReadOnly && isNullOrUndefined(row.price)}>Standard</If>
								<If condition={isReadOnly && isNotNullOrUndefined(row.price)}>
									{formatCurrency(row.price!)}
								</If>
								<If condition={!isReadOnly}>
									<Input
										className="pricing-input custom-input"
										input={{ onWheel: (e: any) => e.target.blur() }}
										type="number"
										icon="dollar sign"
										iconPosition="left"
										placeholder="0"
										value={getRowText(row)}
										onChange={(_e, { value }) => handleChange(row, value as string)}
										disabled={isReadOnly}
										error={isNullOrUndefined(row.price) && !!errors?.addOnsPrices}
									/>
								</If>
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
}
